import { config } from "config";

import { featureBranchURL } from "helpers/regexp";
import { IKeyValue, ParamValue, stringifyQueryParams } from "helpers/url";
import { KeyMap } from "utils/interface";

export function redirect(url: string): void {
  window.location.href = url;
}

/**@deprecated */
export function navigate(_: unknown): void {}

export function serializeAccountsStateParam(queryObject: KeyMap<string>): string {
  const query: KeyMap<string> = {};
  Object.keys(queryObject).forEach(key => {
    query[key] = encodeURIComponent(queryObject[key]);
  });
  return JSON.stringify(query);
}

export function deserializeAccountsStateParam(stateParam: string): KeyMap<string> {
  try {
    const parsedState = JSON.parse(stateParam);
    return Object.keys(parsedState).reduce<KeyMap<string>>(
      (acc, key) => ({
        ...acc,
        [key]: decodeURIComponent(parsedState[key]),
      }),
      {}
    );
  } catch {
    return {};
  }
}

function getPathAfterHost(): string {
  return window.location.pathname + window.location.search;
}

interface IRedirectToAccountsOptions {
  canceled?: boolean;
  signout?: boolean;
  ghost?: boolean;
  couponCode?: string;
  signup?: boolean;
}

function getFeatureBranchAwareAccountParams(
  params: {
    redirect_uri: string;
  } & IKeyValue<ParamValue>,
  featureBranchPart: string
): {
  redirect_uri: string;
} & IKeyValue<ParamValue> {
  if (!featureBranchPart) {
    return params;
  }

  return {
    ...params,
    redirect_uri: `${params.redirect_uri}${featureBranchPart}`,
  };
}

export function redirectToAccounts(options: IRedirectToAccountsOptions = {}): void {
  const pathAfterHost = getPathAfterHost();
  const url = getRedirectToAccountsPath(pathAfterHost, options);

  redirect(url);
}

export function getRedirectToAccountsPath(currentPath: string, options: IRedirectToAccountsOptions = {}): string {
  const featureBranchMatch = featureBranchURL.exec(currentPath);
  const featureBranchPart = featureBranchMatch ? featureBranchMatch[1] : "";
  const redirectTo = (featureBranchMatch ? featureBranchMatch[2] : currentPath).substring(1);

  const accountsParams = getFeatureBranchAwareAccountParams(config.accountsParams, featureBranchPart);

  const queryString: KeyMap<string> = {};
  if (redirectTo && !options.signout) {
    queryString.state = serializeAccountsStateParam({
      redirectTo,
    });
  }
  if (options.canceled) {
    queryString.authentication_exception = "account_canceled";
  } else if (options.signout) {
    queryString.authentication_exception = "identity_lost";
  }

  if (options.couponCode) {
    queryString.coupon_code = options.couponCode;
  }

  if (!accountsParams.response_type) {
    accountsParams.response_type = "token";
  }

  if (!accountsParams.client_id) {
    accountsParams.client_id = config.helpdeskClientId;
  }

  return `${config.globalAccounts}${options.signup ? "/signup" : ""}?${stringifyQueryParams(accountsParams)}&${stringifyQueryParams(queryString)}`;
}

export function shouldOpenNewWindow(e: React.MouseEvent<HTMLElement>): boolean {
  return e.button !== 0 || e.ctrlKey || e.metaKey;
}

export function setDocumentTitle(title: string): void {
  document.title = title;
}

export function extractRouteFromPathname(pathname: string): string | undefined {
  return pathname.split("/").filter(Boolean).pop()?.split("?")[0].split("#")[0];
}

export const openLink = (url: string, { targetBlank = true }): void => {
  const temporaryAnchor = document.createElement("a");
  temporaryAnchor.setAttribute("href", url);

  if (targetBlank) {
    temporaryAnchor.setAttribute("target", "_blank");
  }

  temporaryAnchor.setAttribute("rel", "nofollow noreferrer noopener");
  document.body.appendChild(temporaryAnchor);
  temporaryAnchor.click();
  document.body.removeChild(temporaryAnchor);
};

export const openInCard = (url: string): void => openLink(url, { targetBlank: false });
export const openInNewCard = (url: string): void => openLink(url, { targetBlank: true });
